import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

const MENU_GAP = 8;
const MENU_PADDING = 6;
const OPTION_GAP = 4;
const VIEWPORT_MARGIN = 12;
const VISIBLE_ROWS = 7;

function getOptionHeight(variant) {
  return variant === "compact" ? 32 : 40;
}

function getPreferredMenuHeight(optionCount, variant) {
  if (optionCount <= 0) {
    return MENU_PADDING * 2;
  }

  const visibleCount = Math.min(optionCount, VISIBLE_ROWS);
  const optionHeight = getOptionHeight(variant);
  return (MENU_PADDING * 2) + (visibleCount * optionHeight) + ((visibleCount - 1) * OPTION_GAP);
}

export default function AccountSettingSelect({
  title,
  value,
  options,
  onChange,
  renderSelectedValue,
  renderOption,
  disabled = false,
  variant = "default",
  onOpen = null
}) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);
  const [menuStyle, setMenuStyle] = useState(null);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value) || options[0] || null,
    [options, value]
  );

  useEffect(() => {
    if (disabled) {
      setIsOpen(false);
    }
  }, [disabled]);

  useEffect(() => {
    if (!isOpen) {
      setMenuStyle(null);
      return undefined;
    }

    const updateMenuPosition = () => {
      const element = containerRef.current;

      if (!element) {
        return;
      }

      const rect = element.getBoundingClientRect();
      const preferredHeight = getPreferredMenuHeight(options.length, variant);
      const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP - VIEWPORT_MARGIN;
      const spaceAbove = rect.top - MENU_GAP - VIEWPORT_MARGIN;
      const shouldOpenUp = spaceBelow < preferredHeight && spaceAbove > spaceBelow;
      const availableHeight = shouldOpenUp ? spaceAbove : spaceBelow;
      const maxHeight = Math.max(Math.min(preferredHeight, availableHeight), getOptionHeight(variant) + (MENU_PADDING * 2));

      setMenuStyle({
        position: "fixed",
        left: rect.left,
        width: rect.width,
        maxHeight,
        top: shouldOpenUp ? "auto" : rect.bottom + MENU_GAP,
        bottom: shouldOpenUp ? window.innerHeight - rect.top + MENU_GAP : "auto",
        zIndex: 1600
      });
    };

    updateMenuPosition();
    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [isOpen, options.length, variant]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (containerRef.current?.contains(event.target)) {
        return;
      }

      setIsOpen(false);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  return (
    <div ref={containerRef} className={`account-setting-select-shell${isOpen ? " account-setting-select-shell--open" : ""}`}>
      <button
        type="button"
        className={`account-setting-select-trigger${variant === "compact" ? " account-setting-select-trigger--compact" : ""}`}
        onClick={(event) => {
          event.stopPropagation();
          if (disabled) {
            return;
          }

          setIsOpen((current) => {
            const nextValue = !current;

            if (nextValue && onOpen) {
              onOpen();
            }

            return nextValue;
          });
        }}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={title}
        disabled={disabled}
      >
        <div className="account-setting-select-preview">
          {renderSelectedValue
            ? renderSelectedValue(selectedOption?.value, selectedOption)
            : <span className="account-setting-select-preview-label">{selectedOption?.label || value}</span>}
        </div>
        <span className="account-setting-select-chevron" aria-hidden="true">▾</span>
      </button>

      {isOpen && menuStyle ? createPortal(
        <div
          className="account-setting-select-menu"
          role="listbox"
          aria-label={title}
          style={menuStyle}
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          {options.map((option) => {
            const isSelected = option.value === value;
            const isOptionDisabled = Boolean(option.disabled);

            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`account-setting-select-option${isSelected ? " account-setting-select-option--selected" : ""}${variant === "compact" ? " account-setting-select-option--compact" : ""}${isOptionDisabled ? " account-setting-select-option--disabled" : ""}`}
                onClick={() => {
                  if (isOptionDisabled) {
                    return;
                  }

                  onChange(option.value);
                  setIsOpen(false);
                }}
                disabled={isOptionDisabled}
              >
                <div className="account-setting-select-option-content">
                  {renderOption
                    ? renderOption(option.value, option)
                    : <span className="account-setting-select-preview-label">{option.label}</span>}
                </div>
                {isSelected ? <span className="account-setting-select-option-check" aria-hidden="true">✓</span> : null}
              </button>
            );
          })}
        </div>,
        document.body
      ) : null}
    </div>
  );
}